"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import gsap from "gsap";
import { MagneticButton } from "./Effects";
import { useInstallTarget, withUtm } from "../lib/install";
import { BrowserIcon } from "./BrowserIcon";

const TYPEWRITER_WORDS = [
  "8D Rotation",
  "Surround Sound",
  "HRTF 3D",
  "Binaural Beats",
  "Concert Hall",
  "Bass Boost",
  "Lossless Export",
  "Video Clips",
  "Auto DJ Mixes",
];

function TypewriterLine() {
  const [wordIndex, setWordIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [text, setText] = useState("");
  const [reduced, setReduced] = useState(false);
  const pauseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    // Clear the 1800ms end-of-word pause on unmount — it used to fire
    // setState after unmount.
    return () => {
      if (pauseRef.current) clearTimeout(pauseRef.current);
    };
  }, []);

  const tick = useCallback(() => {
    const currentWord = TYPEWRITER_WORDS[wordIndex];
    if (!deleting) {
      setText(currentWord.slice(0, charIndex + 1));
      if (charIndex + 1 === currentWord.length) {
        pauseRef.current = setTimeout(() => setDeleting(true), 1800);
        return;
      }
      setCharIndex((c) => c + 1);
    } else {
      setText(currentWord.slice(0, charIndex));
      if (charIndex === 0) {
        setDeleting(false);
        setWordIndex((i) => (i + 1) % TYPEWRITER_WORDS.length);
        return;
      }
      setCharIndex((c) => c - 1);
    }
  }, [wordIndex, charIndex, deleting]);

  useEffect(() => {
    if (reduced) return;
    const speed = deleting ? 40 : 80;
    const timer = setTimeout(tick, speed);
    return () => clearTimeout(timer);
  }, [tick, deleting, reduced]);

  return (
    <div className="mt-3 h-8 flex items-center justify-center">
      <span className="inline-flex items-center text-sm font-bold uppercase tracking-[2px] text-[#FF9F1C]/70">
        {reduced ? TYPEWRITER_WORDS[0] : text}
        <span aria-hidden="true" className="inline-block w-[2px] h-[1.1em] bg-[#FF9F1C] ml-0.5 animate-pulse" />
      </span>
    </div>
  );
}

/* Deterministic pseudo-random per bar index — identical on server and
   client, so SSR markup matches hydration exactly (Math.random() here
   caused a hydration mismatch on every load). */
function barHeight(i: number) {
  const jitter = ((i * 7919) % 141) / 10; // 0–14, stable per index
  return Math.max(5, 14 + Math.sin(i * 0.35) * 22 + jitter);
}

export default function Hero() {
  const containerRef = useRef<HTMLDivElement>(null);
  const barsRef = useRef<HTMLDivElement>(null);
  const install = useInstallTarget();

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      if (barsRef.current && !prefersReduced) {
        const bars = barsRef.current.querySelectorAll(".wave-bar");
        tl.from(bars, { scaleY: 0, duration: 0.6, stagger: 0.02 }, 0);
        // One batched tween instead of 36 independent ones. repeatRefresh
        // re-rolls the random targets each cycle so bars don't ping-pong
        // between two fixed values; the 0.7s delay lets the intro finish.
        gsap.to(bars, {
          scaleY: "random(0.25, 1)",
          duration: "random(0.35, 0.7)",
          repeat: -1, yoyo: true, repeatRefresh: true,
          ease: "sine.inOut", stagger: 0.04, delay: 0.7,
        });
      }

      if (prefersReduced) {
        // Content appears instantly; no entrance choreography, no loops.
        gsap.set("[data-hero-title] span, [data-hero-sub], [data-hero-fade]", { clearProps: "all" });
        return;
      }

      tl.from("[data-hero-title] span", {
        y: 100, rotateX: -90, opacity: 0,
        duration: 0.9, stagger: 0.04,
      }, 0.1);

      tl.from("[data-hero-sub]", {
        y: 30, opacity: 0, duration: 0.7,
      }, 0.5);

      tl.from("[data-hero-fade]", {
        y: 20, opacity: 0, duration: 0.5, stagger: 0.08,
      }, 0.7);

      gsap.to("[data-glow-1]", { scale: 1.15, opacity: 0.06, duration: 4, repeat: -1, yoyo: true, ease: "sine.inOut" });
      gsap.to("[data-glow-2]", { scale: 1.2, opacity: 0.04, duration: 5, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 1 });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const titleChars = "8Dfy".split("");

  return (
    <section
      ref={containerRef}
      className="relative min-h-svh flex flex-col items-center justify-center px-6 pt-24 pb-16 overflow-hidden"
    >
      {/* Animated glow blobs */}
      <div data-glow-1 className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#FF3366]/[0.04] blur-[140px] pointer-events-none" />
      <div data-glow-2 className="absolute top-1/2 left-1/4 w-[350px] h-[350px] rounded-full bg-[#FF9F1C]/[0.03] blur-[100px] pointer-events-none" />

      {/* Waveform */}
      <div ref={barsRef} className="flex items-end justify-center gap-[3px] h-12 mb-10" aria-hidden="true">
        {Array.from({ length: 36 }).map((_, i) => (
          <div
            key={i}
            className="wave-bar w-[3px] rounded-full origin-bottom"
            style={{
              height: `${barHeight(i).toFixed(2)}px`,
              background: i < 10 ? "#FF3366" : i < 26 ? `linear-gradient(to top, #FF3366, #FF9F1C)` : "#FF9F1C",
              opacity: +(0.45 + (i / 36) * 0.55).toFixed(4),
            }}
          />
        ))}
      </div>

      {/* Brand glyph (visual title; the real H1 is the keyword line below) */}
      <div
        data-hero-title
        className="text-5xl sm:text-6xl md:text-8xl font-black tracking-tighter text-center leading-[0.9]"
        style={{ perspective: "600px" }}
      >
        {titleChars.map((ch, i) => (
          <span
            key={i}
            className="inline-block bg-gradient-to-r from-[#FF3366] via-[#FF9F1C] to-[#FF3366] bg-clip-text text-transparent bg-[length:200%_auto]"
          >
            {ch}
          </span>
        ))}
      </div>

      <h1 data-hero-sub className="mt-4 text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-center text-white/90">
        Turn YouTube, Spotify and Apple Music into <span className="text-[#FF3366]">8D</span> sound
      </h1>

      <TypewriterLine />

      <p data-hero-fade className="mt-6 text-sm text-[#b3b3b3] text-center max-w-lg leading-relaxed">
        A browser extension that wraps sound around your head.
        <br className="hidden sm:block" />
        Spatial surround, 3D positioning, binaural beats, Auto DJ.
        <br className="hidden sm:block" />
        Put on headphones. Hit play. That&apos;s it.
      </p>

      {/* CTAs */}
      <div data-hero-fade className="flex flex-wrap items-center justify-center gap-4 mt-10">
        <MagneticButton
          href={withUtm(install.baseUrl, "hero")}
          target="_blank"
          rel="noopener noreferrer"
          title={`Install 8Dfy free for ${install.shortLabel}`}
          className="group relative px-8 py-3.5 rounded-full font-bold text-xs uppercase tracking-[1.8px] text-white overflow-hidden transition-shadow duration-300 shadow-[0_4px_24px_rgba(255,51,102,0.25)] hover:shadow-[0_6px_32px_rgba(255,51,102,0.4)]"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#FF3366] to-[#FF9F1C] rounded-full" />
          <span className="relative flex items-center gap-2 text-white">
            <BrowserIcon browser={install.browser} className="w-4 h-4" />
            Install Free for {install.shortLabel}
          </span>
        </MagneticButton>
        <MagneticButton
          href="#pricing"
          title="See 8Dfy Pro features and pricing"
          className="px-8 py-3 rounded-full font-bold text-xs uppercase tracking-[1.8px] text-[#b3b3b3] border border-[#4d4d4d] hover:border-[#7c7c7c] hover:text-white transition-all duration-200"
        >
          See Pro Features
        </MagneticButton>
      </div>

      {/* Proof */}
      <p data-hero-fade className="mt-10 text-[10px] text-[#b3b3b3]/75 tracking-[2px] uppercase">
        YouTube &middot; Spotify Web &middot; Apple Music &middot; 100% client-side
      </p>

      {/* Scroll indicator — decorative; hidden on short/mobile viewports */}
      <div data-hero-fade aria-hidden="true" className="hidden sm:block absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="w-5 h-8 rounded-full border border-[#4d4d4d] flex items-start justify-center p-1.5">
          <div className="w-1 h-2 rounded-full bg-[#FF3366]/60 animate-bounce" />
        </div>
      </div>
    </section>
  );
}
